import React from "react";
import { Box, Button, Card, CardContent, Divider, Typography, useTheme } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import { NarrativeTemplate } from "../../../shared/interfaces/ReportGenReport";
import { cardStyles, cardContentStyles, sectionHeaderStyles } from "../styles";
import { ReportTypeSelector } from "./ReportTypeSelector";
import { NarrativeExample } from "./NarrativeExample";
import { GenerateReportActions } from "./GenerateReportActions";

interface Props {
  reportType: NarrativeTemplate;
  generalReportType: NarrativeTemplate;
  customReportTypes?: NarrativeTemplate[];
  onReportTypeChange: (value: NarrativeTemplate) => void;
  selectedFields: string[];
  onClearSelectedFields: () => void;
  onGenerate: () => void;
  isGenerating: boolean;
  isMaxJobsReached: boolean;
  generateDisabled: boolean;
  selectedFieldsCount: number;
  viewedCount: number;
  totalVideos: number;
}

export const ReportConfigSection: React.FC<Props> = ({
  reportType,
  generalReportType,
  customReportTypes,
  onReportTypeChange,
  selectedFields,
  onClearSelectedFields,
  onGenerate,
  isGenerating,
  isMaxJobsReached,
  generateDisabled,
  selectedFieldsCount,
  viewedCount,
  totalVideos,
}) => {
  const theme = useTheme();

  return (
    <Card {...cardStyles(theme)} sx={{ ...cardStyles(theme).sx, height: "auto" }}>
      <CardContent {...cardContentStyles}>
        <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 32,
              height: 32,
              borderRadius: "50%",
              bgcolor: theme.palette.primary.main,
              color: "white",
              fontWeight: 700,
              fontSize: "1rem",
              flexShrink: 0,
            }}
          >
            2
          </Box>
          <Typography {...sectionHeaderStyles(theme)}>Select Narrative Template</Typography>
        </Box>
        <Divider sx={{ mb: 3 }} />

        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 3 }}>
          <ReportTypeSelector
            reportType={reportType}
            generalReportType={generalReportType}
            customReportTypes={customReportTypes}
            onChange={onReportTypeChange}
          />

          <NarrativeExample example={reportType.examples?.[0]} />

          {selectedFields.length > 0 && (
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                onClick={onClearSelectedFields}
                variant="outlined"
                size="small"
                startIcon={<ClearIcon />}
                sx={{
                  minWidth: "auto",
                  px: 2,
                  py: 0.75,
                  fontSize: "0.8rem",
                  borderRadius: 2,
                  borderColor: theme.palette.warning.main,
                  color: theme.palette.warning.main,
                  textTransform: "none",
                  "&:hover": {
                    borderColor: theme.palette.warning.dark,
                    backgroundColor: theme.palette.warning.main + "14",
                    color: theme.palette.warning.dark,
                  },
                }}
              >
                Clear ({selectedFields.length})
              </Button>
            </Box>
          )}

          <GenerateReportActions
            onGenerate={onGenerate}
            isGenerating={isGenerating}
            isMaxJobsReached={isMaxJobsReached}
            generateDisabled={generateDisabled}
            selectedFieldsCount={selectedFieldsCount}
            viewedCount={viewedCount}
            totalVideos={totalVideos}
          />
        </Box>
      </CardContent>
    </Card>
  );
};
